import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";
import { Text } from "konva/lib/shapes/Text";
import Group = Konva.Group;
import Ellipse = Konva.Ellipse;
import Rect = Konva.Rect;
import Path = Konva.Path;

export class PipelineKonva {
  static readonly COLOR_CODE = "#CC6600";

  static init(pipeline: Pipeline): Group {
    const parentGroup = new Konva.Group();

    const pipelineGroup = new Konva.Group({
      x: pipeline.x,
      y: pipeline.y,
      rotation: pipeline.rotation,
      diameter: pipeline.diameter,
      medium: pipeline.medium,
      temperature: pipeline.temperature,
      material: pipeline.material,
      draggable: false,
    });
    pipelineGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, pipeline);
    pipelineGroup.setAttr("minWidth", 50);
    pipelineGroup.setAttr("minHeight", 20);

    const mid = new Konva.Rect({ fill: PipelineKonva.COLOR_CODE, opacity: 0.5 });
    const front = new Konva.Ellipse({
      radiusX: 0, // actual value set in setShapeDimensions
      radiusY: 0, // actual value set in setShapeDimensions
      fill: "white",
      stroke: PipelineKonva.COLOR_CODE,
      strokeWidth: 0.5,
    });
    const back = new Konva.Path({ fill: PipelineKonva.COLOR_CODE, opacity: 0.5 });
    this.setShapeDimensions(pipeline.width, pipeline.height, pipelineGroup, mid, front, back);

    const itemText = FloorplanItemTextKonva.init(pipeline);
    const text = FloorplanItemTextKonva.getText(itemText);

    this.scaleText(text, pipelineGroup);
    FloorplanItemTextKonva.updateBackground(itemText);

    pipelineGroup.on("transformstart", () => {
      itemText.hide();
    });

    pipelineGroup.on("transform", () => {
      const width = pipelineGroup.width() * pipelineGroup.scaleX();
      const height = pipelineGroup.height() * pipelineGroup.scaleY();

      pipelineGroup.scaleX(1);
      pipelineGroup.scaleY(1);

      this.setShapeDimensions(width, height, pipelineGroup, mid, front, back);
    });

    pipelineGroup.on("transformend", () => {
      pipeline.updateForm(
        pipelineGroup.width(),
        pipelineGroup.height(),
        pipelineGroup.rotation(),
        pipelineGroup.x(),
        pipelineGroup.y(),
      );
      this.scaleText(text, pipelineGroup);
      FloorplanItemTextKonva.updateBackground(itemText);
      this.setTextPosition(itemText, pipelineGroup);
      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    pipelineGroup.on("dragmove", () => {
      this.setTextPosition(itemText, pipelineGroup);
    });

    pipelineGroup.add(mid, front, back);

    parentGroup.add(pipelineGroup, itemText);
    this.setTextPosition(itemText, pipelineGroup);

    return parentGroup;
  }

  static refreshLength(pipeline: Pipeline, parentGroup: Group) {
    const pipelineGroup = parentGroup.getChildren()[0] as Group;
    const mid = pipelineGroup.getChildren()[0] as Rect;
    const front = pipelineGroup.getChildren()[1] as Ellipse;
    const back = pipelineGroup.getChildren()[2] as Path;
    this.setShapeDimensions(pipeline.width, pipeline.height, pipelineGroup, mid, front, back);
    this.refreshItemText(pipeline, parentGroup);
  }

  static refreshItemText(pipeline: Pipeline, group: Group) {
    const positionId = group.getChildren()[1] as Group;
    const pipelineGroup = group.getChildren()[0] as Konva.Group;
    FloorplanItemTextKonva.refreshText(pipeline, positionId);
    this.setTextPosition(positionId, pipelineGroup);
  }

  static setShapeDimensions(width: number, height: number, group: Group, mid: Rect, front: Ellipse, back: Path): void {
    group.width(width);
    group.height(height);

    mid.width(width);
    mid.height(height);

    front.radiusX(height / 4);
    front.radiusY(height / 2 - 0.5);
    front.offsetX(-width);
    front.offsetY(-height / 2);

    const radius = height / 2;
    back.offsetY(-radius);
    back.data(`M 0 ${-radius} A ${-radius} ${radius * 1.2} 0 0 1 0 ${radius}`);
  }

  static setTextPosition(itemText: Group, pipelineGroup: Group): void {
    const text = FloorplanItemTextKonva.getText(itemText);
    text.align("left");
    itemText.offsetY(0);
    itemText.offsetX(0);
    this.setTextByRotation(itemText, pipelineGroup);
  }

  private static scaleText(itemText: Text, pipelineGroup: Group) {
    itemText.fontSize(pipelineGroup.height() / 3);
  }

  static setTextByRotation(itemText: Group, pipelineGroup: Group) {
    const item = pipelineGroup.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(itemText, pipelineGroup, item);
  }
}
